import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { AlertTriangle, ExternalLink, Loader2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useDebounced } from "@/lib/text";
import { Button } from "@/components/ui/button";

// Live CRM duplicate warning (warn + decide, never blocks)
export const DuplicateWarning = ({ form }) => {
  const navigate = useNavigate();
  const key = useDebounced(JSON.stringify([form.name, form.company, form.phone, form.whatsapp, form.email]), 500);
  const [res, setRes] = useState({ strong: [], soft: [] });
  const [dismissed, setDismissed] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const [name, company, phone, whatsapp, email] = JSON.parse(key);
    if (!(phone || whatsapp || email || (name || "").length >= 3 || (company || "").length >= 3)) { setRes({ strong: [], soft: [] }); return; }
    api.post("/customers/check-duplicate", { name, company, phone, whatsapp, email }).then((r) => setRes(r.data)).catch(() => {});
  }, [key]);

  const rows = [...res.strong.map((r) => ({ ...r, strong: true })), ...res.soft];
  const sig = rows.map((r) => r.id).join(",");
  if (!rows.length || dismissed === sig) return null;

  const pickExisting = async (r) => {
    setBusy(true);
    try {
      await api.post(`/customers/${r.id}/activity`, { summary: form.summary || "New enquiry (attached from duplicate check)", screenshots: form.screenshots || [], next_followup_date: form.next_followup_date || null });
      toast.success("Enquiry attached to the existing customer"); navigate(`/customers/${r.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="duplicate-warning" className={`rounded-md border p-3 space-y-2 ${res.strong.length ? "border-amber-300 bg-amber-50" : "border-border bg-secondary/50"}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"><AlertTriangle className={`h-3.5 w-3.5 ${res.strong.length ? "text-amber-600" : "text-muted-foreground"}`} /> {res.strong.length ? "Possible existing customer" : "Similar name found — please check"}</div>
      {rows.slice(0, 4).map((r) => (
        <div key={r.id} data-testid={`dup-row-${r.id}`} className="flex items-center justify-between gap-3 text-xs bg-white/70 rounded px-2.5 py-2">
          <div className="min-w-0">
            <div className="font-semibold truncate">{r.name}{r.company ? ` · ${r.company}` : ""}</div>
            <div className="text-muted-foreground truncate">{[r.phone, r.country].filter(Boolean).join(" · ")} · Entered by {r.created_by_name || "—"}{r.created_at ? ` on ${format(new Date(r.created_at), "MMM d, yyyy")}` : ""} · <span className={r.strong ? "text-amber-700 font-medium" : ""}>{r.signals.join(", ")}</span></div>
          </div>
          <div className="flex gap-1.5 shrink-0">
            {r.can_open ? <>
              <Button type="button" size="sm" variant="outline" data-testid={`dup-open-${r.id}`} onClick={() => navigate(`/customers/${r.id}`)} className="h-7 text-xs gap-1"><ExternalLink className="h-3 w-3" /> Open</Button>
              <Button type="button" size="sm" data-testid={`dup-use-${r.id}`} onClick={() => pickExisting(r)} disabled={busy} className="h-7 text-xs gap-1">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserCheck className="h-3 w-3" />} Use Existing</Button>
            </> : <span className="text-[10px] text-muted-foreground">Owned by {r.consultant_name}</span>}
          </div>
        </div>
      ))}
      <button type="button" data-testid="dup-create-anyway" onClick={() => setDismissed(sig)} className="text-xs text-muted-foreground hover:text-foreground underline">Create New Anyway</button>
    </div>
  );
};
